/**
 * Measurement on the watch page: the registration, landed once, and the talk's
 * moments. Only on the host's own address (the tenant host), never on the
 * app's, and only when the workspace set an id. The mark the form's redirect
 * puts on the address (`new=1`) is taken off whether or not anything loads, so
 * a copied link never carries it on.
 *
 * Meta hears the moments our server sends, at the same positions and with the
 * same event ids (shared/meta-events.ts), so it counts each once. The team's
 * own browsers send Meta nothing.
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import { measurementActive } from "../../shared/measure";
import { marksCrossed, metaContent, metaEventId, metaEventName, metaKey, type MetaStep } from "../../shared/meta-events";
import { COMPLETE_PCT } from "../../shared/tags";
import type { MeasurementConfig, OfferKind, WatchSession } from "../../shared/types";
import {
  freshVideoSent,
  gaEvent,
  installMeasurement,
  measureFlags,
  metaEvent,
  videoEventsFor,
  videoParams,
} from "../lib/measure";
import { isTenantHost } from "../lib/tenant";

const SENT_KEY = (eventId: string) => `fw.meta.${eventId}`;
function sentHere(eventId: string): boolean {
  try {
    return window.localStorage.getItem(SENT_KEY(eventId)) !== null;
  } catch {
    return false;
  }
}
function rememberSent(eventId: string): void {
  try {
    window.localStorage.setItem(SENT_KEY(eventId), "1");
  } catch {
    /* nothing to remember with: Meta still pairs it with the server's copy */
  }
}

const LEAD_KEY = (registrantId: string) => `fw.lead.${registrantId}`;
/** The registration was reported from this browser already: a reload must not report it again. */
function leadOnce(registrantId: string): boolean {
  try {
    if (window.localStorage.getItem(LEAD_KEY(registrantId)) !== null) return false;
    window.localStorage.setItem(LEAD_KEY(registrantId), "1");
  } catch {
    /* nothing to remember with; the mark on the address is gone either way */
  }
  return true;
}

/** The first landing after the form: the mark is on the address, and comes off. */
export function takeFirstLandingMark(): boolean {
  if (typeof window === "undefined") return false;
  const url = new URL(window.location.href);
  if (url.searchParams.get("new") !== "1") return false;
  url.searchParams.delete("new");
  window.history.replaceState(window.history.state, "", url.toString());
  return true;
}

/** Where the playhead is, as the watched percent the server records. */
function pctOf(sec: number, durationSec: number): number {
  return durationSec > 0 ? Math.max(0, Math.min(100, (sec / durationSec) * 100)) : 0;
}

export interface WatchMeasure {
  tick: (step: { to: number; playing: boolean }) => void;
  /** The offer was pressed. */
  press: (kind: OfferKind) => void;
  /** They are at the live's table: the talk started for them. */
  joinedLive: (sessionId: string) => void;
  /** The host ended the live while they were at the table. */
  stayedLive: (sessionId: string) => void;
}

export function useWatchMeasure(
  m: MeasurementConfig | undefined,
  session: WatchSession,
  sentEarlier: readonly string[] = [],
): WatchMeasure {
  const sent = useRef(freshVideoSent());
  const here = typeof window !== "undefined" && isTenantHost(window.location.hostname);
  const on = here && m !== undefined && measurementActive(m);
  const meta = on && Boolean(m?.meta_pixel_id) && !measureFlags().internal;
  const slug = session.slug;
  const webinarId = session.webinar_id;
  const key = useRef<string | null>(null);
  const earlier = useMemo(() => new Set(sentEarlier), [sentEarlier]);

  // Each moment once from this browser, and never one our server sent days ago.
  const fire = useCallback(
    (step: MetaStep, extra?: string | number, detail?: { percent?: number; offerKind?: OfferKind }) => {
      if (!meta || !key.current) return;
      const id = metaEventId(step, key.current, extra);
      if (earlier.has(id) || sentHere(id)) return;
      rememberSent(id);
      const params: Record<string, unknown> = { ...metaContent(slug, webinarId) };
      if (detail?.percent !== undefined) params.percent = detail.percent;
      if (detail?.offerKind) params.offer_kind = detail.offerKind;
      metaEvent(metaEventName(step, { offerKind: detail?.offerKind }), params, id);
    },
    [meta, earlier, slug, webinarId],
  );

  useEffect(() => {
    const fresh = takeFirstLandingMark();
    if (!on || !m) return;
    installMeasurement(m, metaContent(slug, webinarId));
    const lead = fresh && leadOnce(session.registrant_id);
    if (lead) gaEvent("generate_lead", { lead_source: "webinar", webinar: slug });
    let alive = true;
    void metaKey(session.registrant_id).then((k) => {
      if (!alive) return;
      key.current = k;
      if (lead) fire("lead");
    });
    return () => {
      alive = false;
    };
  }, [on, m, session.registrant_id, slug, webinarId, fire]);

  const video = useMemo(
    () => ({ title: session.title, url: session.playback_url, durationSec: session.duration_sec }),
    [session.title, session.playback_url, session.duration_sec],
  );
  // Where the server's record of their place stood when the page opened: each
  // moment is a crossing from there, as the server counts it, skips included.
  const lastPct = useRef(session.watched_pct);
  // The server counted their first play on an earlier visit.
  const startedBefore = session.watched_sec > 0;
  const offerAt = session.offer?.appears_at;
  const tick = useCallback(
    (step: { to: number; playing: boolean }) => {
      if (!on) return;
      for (const ev of videoEventsFor(sent.current, step, video.durationSec)) {
        gaEvent(ev.name, videoParams(video, step.to, ev.percent));
      }
      const pct = pctOf(step.to, video.durationSec);
      const prior = lastPct.current;
      lastPct.current = pct;
      if (step.to > 0 && !startedBefore) fire("view");
      for (const mark of marksCrossed(prior, pct)) fire("progress", mark, { percent: mark });
      if (typeof offerAt === "number" && video.durationSec > 0) {
        const offerPct = pctOf(offerAt, video.durationSec);
        if (prior < offerPct && pct >= offerPct) fire("offer");
      }
      if (prior < COMPLETE_PCT && pct >= COMPLETE_PCT) fire("done");
    },
    [on, video, offerAt, startedBefore, fire],
  );

  const press = useCallback((kind: OfferKind) => fire("press", undefined, { offerKind: kind }), [fire]);
  const joinedLive = useCallback((sessionId: string) => fire("view", sessionId), [fire]);
  const stayedLive = useCallback((sessionId: string) => fire("done", sessionId), [fire]);

  return useMemo(() => ({ tick, press, joinedLive, stayedLive }), [tick, press, joinedLive, stayedLive]);
}
